import axios from "axios";
import { toast } from "../hooks/use-toast";
import { toast as sonnerToast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Create a pre-configured axios instance
const api = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  timeout: 60000, // 60 second default timeout (can be overridden per-request)
});

// Add auth interceptor
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// ── Deploy-window resilience layer ────────────────────────────────────────
// During an Emergent backend deploy the pod is recycled and any in-flight
// request can fail for ~30s to 3 min with a 502/503/504/520-524 from
// Cloudflare, or with a plain network error, or with a transient 401 if
// the new pod hasn't fully loaded the JWT secret yet.
//
// Strategy (battle-tested over multiple post-deploy support tickets):
//
// 1. TRANSIENT GATEWAY (502/503/504/521/522 — origin unreached):
//    Retry up to 10× with backoff (1s→30s, ~3 min total). Safe for any
//    method because we KNOW the origin never processed the request.
//
// 2. ORIGIN-TOUCHED GATEWAY (520/523/524/525/526/527 — origin partial):
//    Retry up to 10× for READS only. Never retry writes — might double-write.
//
// 3. PURE NETWORK ERROR (no response): treat as transient gateway, retry.
//
// 4. 401 UNAUTHORIZED: don't immediately nuke the token. Probe /auth/me
//    first — if the wristband is still valid we hit a blip, retry the
//    original request. Only log out if /auth/me ALSO returns 401.
//
// 5. ONE PERSISTENT TOAST: instead of spamming "Resyncing…" on every retry,
//    show one calm amber pill that stays for the whole reconnection window,
//    auto-dismissing on first success or final failure.
//
// 6. CANCELLED REQUESTS: never retry.

const ORIGIN_TOUCHED_STATUSES = new Set([520, 523, 524, 525, 526, 527]);
const ORIGIN_UNREACHED_STATUSES = new Set([502, 503, 504, 521, 522]);
const ALL_TRANSIENT = new Set([...ORIGIN_TOUCHED_STATUSES, ...ORIGIN_UNREACHED_STATUSES]);

// Tiered retry policy — generous budget for transient deploys, no change
// for genuinely-broken requests so debugging stays fast.
const MAX_RETRIES = 10;
const RETRY_DELAYS_MS = [1000, 2000, 4000, 8000, 16000, 30000, 30000, 30000, 30000, 30000];

const isRetryableError = (error) => {
  if (axios.isCancel(error)) return false;
  const config = error.config;
  if (!config) return false;
  const attempts = config.__qsRetryCount || 0;
  if (attempts >= MAX_RETRIES) return false;
  const method = (config.method || "get").toLowerCase();
  const isRead = method === "get";
  const status = error.response?.status;
  // Pure network error (no response) — safe to retry for ANY method.
  if (!error.response) return true;
  // Origin-unreached gateway statuses — safe to retry for any method.
  if (ORIGIN_UNREACHED_STATUSES.has(status)) return true;
  // Origin-touched statuses — only retry for reads (writes might
  // have partially processed).
  if (ORIGIN_TOUCHED_STATUSES.has(status) && isRead) return true;
  return false;
};

// ── Persistent "Reconnecting…" toast ────────────────────────────────────
// One amber pill for the whole reconnection window. Auto-clears on first
// successful response or final failure.
const RECONNECT_TOAST_ID = "qs-reconnecting";
let reconnectToastActive = false;

const showReconnectingToast = () => {
  if (reconnectToastActive) return;
  reconnectToastActive = true;
  try {
    sonnerToast.loading("Reconnecting to server — your work is safe…", {
      id: RECONNECT_TOAST_ID,
      duration: Infinity,
    });
  } catch (_e) { /* sonner not mounted yet — silent */ }
};

const clearReconnectingToast = (succeeded) => {
  if (!reconnectToastActive) return;
  reconnectToastActive = false;
  try {
    if (succeeded) {
      sonnerToast.success("Reconnected", {
        id: RECONNECT_TOAST_ID,
        duration: 2200,
      });
    } else {
      sonnerToast.dismiss(RECONNECT_TOAST_ID);
    }
  } catch (_e) { /* sonner not mounted — silent */ }
};

const showDeployWindowToast = () => {
  // Friendly fallback for non-retryable transient errors (e.g. POST hit
  // a Cloudflare 520 — origin state unknown, can't auto-retry). Caller
  // still gets the rejection; this just replaces the scary raw HTML.
  try {
    sonnerToast.error("The server is just waking up — please try again in a moment.", {
      id: "deploy-window-warning",
      duration: 5500,
    });
  } catch (_e) { /* sonner not mounted yet — silent */ }
};

// ── 401 grace check ─────────────────────────────────────────────────────
// During a deploy, a transient 401 can leak through if the new pod hasn't
// fully loaded the JWT secret yet. Before nuking the user's session we
// probe /auth/me — if the token is fine, the original 401 was a blip and
// we retry. Only log out if /auth/me ALSO returns 401.
//
// Three outcomes (critical — was a 2-state boolean before, which wrongly
// logged users out when the backend was unreachable. Real-world incident
// June 30 2026: cold pod returned 520 on /auth/me probe, was interpreted
// as "token dead", user was kicked to /login mid-session):
//   "valid"     → 2xx response. Token is fine, replay original request.
//   "dead"      → 401 response. Token is genuinely invalid, log out.
//   "uncertain" → network error, 5xx, 520-524, or timeout. Backend is
//                 unreachable; we CAN'T know if the token is dead. Keep
//                 the user logged in and reject the original request —
//                 the next request will re-probe.
//
// Memoization: if 50 in-flight requests all 401 simultaneously during a
// deploy window, we only fire ONE probe — every other 401 reuses the
// same in-flight Promise.
let inFlightAuthProbe = null;
const checkAuthStillValid = async () => {
  if (inFlightAuthProbe) return inFlightAuthProbe;
  inFlightAuthProbe = (async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return "dead";
      const probe = await axios.get(`${BACKEND_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 10000,
        __qsAuthProbe: true,
      });
      if (probe.status >= 200 && probe.status < 300) return "valid";
      return "uncertain";
    } catch (e) {
      // Only a confirmed 401 means the token is dead. Anything else
      // (network error, 5xx, gateway error, timeout) = backend is
      // unreachable and we can't make a decision — keep the user
      // logged in optimistically.
      if (e.response?.status === 401) return "dead";
      return "uncertain";
    } finally {
      setTimeout(() => { inFlightAuthProbe = null; }, 0);
    }
  })();
  return inFlightAuthProbe;
};

const forceLogout = () => {
  localStorage.removeItem("token");
  window.location.href = "/login";
};

// ── Response interceptor ────────────────────────────────────────────────
api.interceptors.response.use(
  (response) => {
    // First successful response after a reconnect period → clear the toast.
    if (reconnectToastActive) clearReconnectingToast(true);
    return response;
  },
  async (error) => {
    const config = error.config;
    const status = error.response?.status;

    // ── 401 handler with grace check ────────────────────────────────────
    if (status === 401) {
      // Never grace-check the probe itself — would infinite-loop.
      if (config?.__qsAuthProbe) {
        forceLogout();
        return Promise.reject(error);
      }
      // Never grace-check a 401 on /auth/me itself — that IS the source of truth.
      const url = config?.url || "";
      if (url.includes("/auth/me")) {
        forceLogout();
        return Promise.reject(error);
      }
      // Already grace-retried once → don't loop, just fail (keep user
      // logged in intentionally — the next FRESH 401 will re-probe).
      if (config?.__qsAuthGraceRetried) {
        return Promise.reject(error);
      }
      // Probe /auth/me to see if the token is dead, alive, or unknowable.
      const probeResult = await checkAuthStillValid();
      if (probeResult === "valid") {
        // Wristband fine — replay original request once.
        config.__qsAuthGraceRetried = true;
        return api(config);
      }
      if (probeResult === "dead") {
        // Token genuinely dead → log out.
        forceLogout();
        return Promise.reject(error);
      }
      // probeResult === "uncertain" — backend is unreachable. We can't
      // tell if the token is dead, so DON'T log the user out. Just
      // reject the original request. The user stays on their page; the
      // NEXT request will re-probe when the backend is back.
      return Promise.reject(error);
    }

    // ── Tier-limit hit ──────────────────────────────────────────────────
    // Server returns 403 with a "monthly limit" message when a free
    // (or Solo, etc.) user tries to create past their cap. We pop ONE
    // sonner toast with a one-tap Upgrade action.
    const detail = error.response?.data?.detail;
    if (
      status === 403 &&
      typeof detail === "string" &&
      /monthly limit of/i.test(detail) &&
      !config?.__qsLimitToastShown
    ) {
      try {
        if (config) config.__qsLimitToastShown = true;
        sonnerToast.error(detail, {
          id: "tier-limit-reached",
          duration: 9000,
          action: {
            label: "Upgrade",
            onClick: () => {
              window.location.href = "/settings?section=subscription";
            },
          },
        });
      } catch (_e) { /* sonner not mounted — silent fail */ }
    }

    // ── Transient-error retry layer ─────────────────────────────────────
    if (isRetryableError(error)) {
      const attempts = config.__qsRetryCount || 0;
      config.__qsRetryCount = attempts + 1;
      const delay = RETRY_DELAYS_MS[Math.min(attempts, RETRY_DELAYS_MS.length - 1)];
      // Show the persistent reconnecting pill on first retry.
      if (attempts === 0) showReconnectingToast();
      await new Promise((resolve) => setTimeout(resolve, delay));
      try {
        return await api(config);
      } catch (retryErr) {
        return Promise.reject(retryErr);
      }
    }

    // ── Final failure path ──────────────────────────────────────────────
    // If we'd been retrying and exhausted the budget, dismiss the
    // reconnecting toast silently (the calling code will surface its own error).
    if (reconnectToastActive) clearReconnectingToast(false);

    // Non-retryable transient (e.g. a write that hit Cloudflare 520) →
    // friendly toast instead of raw Cloudflare HTML.
    if (status && ALL_TRANSIENT.has(status) && !config?.__qsRetryCount) {
      showDeployWindowToast();
    }

    return Promise.reject(error);
  }
);

// Legacy helper kept for callers that imported `toast` indirectly via api.
// (Unused in this file but referenced by some hooks during boot.)
export { toast };
export default api;
